// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Upload, AlertTriangle, ChevronDown, ChevronUp, Loader2, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useLoginActions,
  generateNostrConnectParams,
  generateNostrConnectURI,
  type NostrConnectParams,
} from '@/hooks/useLoginActions';
import { useIsMobile } from '@/hooks/useIsMobile';

interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
}

const validateNsec = (nsec: string) => {
  return /^nsec1[a-zA-Z0-9]{58}$/.test(nsec);
};

const validateBunkerUri = (uri: string) => {
  return uri.startsWith('bunker://') || uri.startsWith('nostrconnect://');
};

const LoginDialog: React.FC<LoginDialogProps> = ({ isOpen, onClose, onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [nsec, setNsec] = useState('');
  const [bunkerUri, setBunkerUri] = useState('');
  const [nostrConnectParams, setNostrConnectParams] = useState<NostrConnectParams | null>(null);
  const [nostrConnectUri, setNostrConnectUri] = useState<string>('');
  const [isWaitingForConnect, setIsWaitingForConnect] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showBunkerInput, setShowBunkerInput] = useState(false);
  const [errors, setErrors] = useState<{
    nsec?: string;
    bunker?: string;
    file?: string;
    extension?: string;
  }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const login = useLoginActions();

  // Check if on mobile (for showing signer app button)
  const isMobile = useIsMobile();
  // Extension only available on web when window.nostr exists
  const hasExtension = 'nostr' in window;

  // Generate nostrconnect params (sync) - just creates the QR code data
  const generateConnectSession = useCallback(() => {
    const relayUrl = login.getRelayUrl();
    const params = generateNostrConnectParams([relayUrl]);
    const uri = generateNostrConnectURI(params, 'Inkwell');
    setNostrConnectParams(params);
    setNostrConnectUri(uri);
    setConnectError(null);
  }, [login]);

  // Start listening for connection (async) - runs after params are set
  useEffect(() => {
    if (!nostrConnectParams || isWaitingForConnect) return;

    const startListening = async () => {
      setIsWaitingForConnect(true);
      abortControllerRef.current = new AbortController();

      try {
        await login.nostrconnect(nostrConnectParams);
        onLogin();
        onClose();
      } catch (error) {
        console.error('Nostrconnect failed:', error);
        setConnectError(error instanceof Error ? error.message : 'Connection failed');
        setIsWaitingForConnect(false);
      }
    };

    startListening();
  }, [nostrConnectParams, login, onLogin, onClose, isWaitingForConnect]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setNostrConnectParams(null);
      setNostrConnectUri('');
      setIsWaitingForConnect(false);
      setConnectError(null);
      setNsec('');
      setBunkerUri('');
      setErrors({});
      setShowBunkerInput(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  // Retry connection with new params
  const handleRetry = useCallback(() => {
    setNostrConnectParams(null);
    setNostrConnectUri('');
    setIsWaitingForConnect(false);
    setConnectError(null);
    // Generate new session after state clears
    setTimeout(() => generateConnectSession(), 0);
  }, [generateConnectSession]);

  const handleCopyUri = async () => {
    await navigator.clipboard.writeText(nostrConnectUri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Open the nostrconnect URI in the system - this will launch a signer app like Amber if installed
  const handleOpenSignerApp = () => {
    if (!nostrConnectUri) return;
    // On Android/iOS, this will trigger the system to find an app that handles nostrconnect:// URIs
    window.location.href = nostrConnectUri;
  };

  const handleExtensionLogin = async () => {
    setIsLoading(true);
    setErrors(prev => ({ ...prev, extension: undefined }));

    try {
      if (!('nostr' in window)) {
        throw new Error('Nostr extension not found. Please install a NIP-07 extension.');
      }
      await login.extension();
      onLogin();
      onClose();
    } catch (e: unknown) {
      const error = e as Error;
      console.error('Extension login failed:', error);
      setErrors(prev => ({
        ...prev,
        extension: error instanceof Error ? error.message : 'Extension login failed'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const executeLogin = (key: string) => {
    setIsLoading(true);
    setErrors({});

    // Use a timeout to allow the UI to update before the synchronous login call
    setTimeout(() => {
      try {
        login.nsec(key);
        onLogin();
        onClose();
      } catch {
        setErrors({ nsec: "Failed to login with this key. Please check that it's correct." });
        setIsLoading(false);
      }
    }, 50);
  };

  const handleKeyLogin = () => {
    if (!nsec.trim()) {
      setErrors(prev => ({ ...prev, nsec: 'Please enter your secret key' }));
      return;
    }

    if (!validateNsec(nsec)) {
      setErrors(prev => ({ ...prev, nsec: 'Invalid secret key format. Must be a valid nsec starting with nsec1.' }));
      return;
    }
    executeLogin(nsec);
  };

  const handleBunkerLogin = async () => {
    if (!bunkerUri.trim()) {
      setErrors(prev => ({ ...prev, bunker: 'Please enter a bunker URI' }));
      return;
    }

    if (!validateBunkerUri(bunkerUri)) {
      setErrors(prev => ({ ...prev, bunker: 'Invalid URI format. Must start with bunker:// or nostrconnect://' }));
      return;
    }

    setIsLoading(true);
    setErrors(prev => ({ ...prev, bunker: undefined }));

    try {
      await login.bunker(bunkerUri);
      onLogin();
      onClose();
      // Clear the URI from memory
      setBunkerUri('');
    } catch {
      setErrors(prev => ({
        ...prev,
        bunker: 'Failed to connect to bunker. Please check the URI.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsFileLoading(true);
    setErrors({});

    const reader = new FileReader();
    reader.onload = (event) => {
      setIsFileLoading(false);
      const content = event.target?.result as string;
      if (content) {
        const trimmedContent = content.trim();
        if (validateNsec(trimmedContent)) {
          executeLogin(trimmedContent);
        } else {
          setErrors({ file: 'File does not contain a valid secret key.' });
        }
      } else {
        setErrors({ file: 'Could not read file content.' });
      }
    };
    reader.onerror = () => {
      setIsFileLoading(false);
      setErrors({ file: 'Failed to read file.' });
    };
    reader.readAsText(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90dvh] p-0 gap-0 overflow-hidden rounded-2xl">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-center">
            Log in
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            Access your account securely
          </DialogDescription>
        </DialogHeader>

        <div className='px-6 pb-6 space-y-4 overflow-y-auto'>
          <Tabs
            defaultValue={hasExtension ? 'extension' : 'connect'}
            className='w-full'
            onValueChange={(value) => {
              if (value === 'connect' && !nostrConnectParams && !connectError) {
                generateConnectSession();
              }
            }}
          >
            <TabsList className={`grid w-full mb-4 ${hasExtension ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {hasExtension && <TabsTrigger value='extension'>Extension</TabsTrigger>}
              <TabsTrigger value='connect'>Connect</TabsTrigger>
              <TabsTrigger value='key'>Nsec</TabsTrigger>
            </TabsList>

            {hasExtension && (
              <TabsContent value='extension' className='space-y-4'>
                <div className='text-center p-4 rounded-lg bg-muted/50'>
                  <div className='text-4xl mb-3'>🔐</div>
                  <p className='text-sm text-muted-foreground mb-4'>
                    Login with one click using your browser extension
                  </p>
                  {errors.extension && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{errors.extension}</AlertDescription>
                    </Alert>
                  )}
                  <Button
                    className='w-full'
                    onClick={handleExtensionLogin}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Login with Extension'}
                  </Button>
                </div>
              </TabsContent>
            )}

            <TabsContent value='connect' className='space-y-4'>
              {/* Nostrconnect Section */}
              <div className='flex flex-col items-center space-y-4'>
                {connectError ? (
                  <div className='flex flex-col items-center space-y-4 py-4'>
                    <p className='text-sm text-red-500 text-center'>{connectError}</p>
                    <Button variant='outline' onClick={handleRetry}>
                      Try Again
                    </Button>
                  </div>
                ) : nostrConnectUri ? (
                  <>
                    {/* QR Code - only show on desktop */}
                    {!isMobile && (
                      <div className='p-4 bg-white rounded-xl'>
                        <QRCodeSVG
                          value={nostrConnectUri}
                          size={180}
                          level='M'
                          includeMargin={false}
                        />
                      </div>
                    )}

                    {/* Status message */}
                    <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                      {isWaitingForConnect ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          <span>Waiting for connection...</span>
                        </>
                      ) : (
                        <span>{isMobile ? 'Open your signer app to connect' : 'Scan with your signer app'}</span>
                      )}
                    </div>

                    {/* Open Signer App button - primary action on mobile */}
                    {isMobile && (
                      <Button
                        className='w-full gap-2'
                        onClick={handleOpenSignerApp}
                      >
                        <ExternalLink className='w-4 h-4' />
                        Open Signer App
                      </Button>
                    )}

                    {/* Copy button */}
                    <Button
                      variant='outline'
                      size={isMobile ? 'default' : 'sm'}
                      className={isMobile ? 'w-full gap-2' : 'gap-2'}
                      onClick={handleCopyUri}
                    >
                      {copied ? (
                        <>
                          <Check className='w-4 h-4' />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className='w-4 h-4' />
                          Copy Connection String
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className='flex items-center justify-center h-[100px]'>
                    <Loader2 className='w-8 h-8 animate-spin text-muted-foreground' />
                  </div>
                )}
              </div>

              {/* Manual URI input section - collapsible */}
              <div className='pt-4 border-t border-border'>
                <button
                  type='button'
                  onClick={() => setShowBunkerInput(!showBunkerInput)}
                  className='flex items-center justify-center gap-2 w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2'
                >
                  <span>Manual bunker connection</span>
                  {showBunkerInput ? (
                    <ChevronUp className='w-4 h-4' />
                  ) : (
                    <ChevronDown className='w-4 h-4' />
                  )}
                </button>

                {showBunkerInput && (
                  <div className='space-y-3 mt-3'>
                    <div className='space-y-2'>
                      <Input
                        id='bunkerUri'
                        value={bunkerUri}
                        onChange={(e) => {
                          setBunkerUri(e.target.value);
                          if (errors.bunker) setErrors(prev => ({ ...prev, bunker: undefined }));
                        }}
                        className='text-sm'
                        placeholder='bunker:// or nostrconnect://'
                      />
                      {errors.bunker && (
                        <p className='text-red-500 text-xs'>{errors.bunker}</p>
                      )}
                    </div>

                    <Button
                      className='w-full'
                      variant='outline'
                      onClick={handleBunkerLogin}
                      disabled={isLoading || !bunkerUri.trim()}
                    >
                      {isLoading ? 'Connecting...' : 'Connect with Bunker'}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value='key' className='space-y-4'>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleKeyLogin();
              }} className='space-y-4'>
                <div className='space-y-2'>
                  <Input
                    id='nsec'
                    type="password"
                    value={nsec}
                    onChange={(e) => {
                      setNsec(e.target.value);
                      if (errors.nsec) setErrors(prev => ({ ...prev, nsec: undefined }));
                    }}
                    className={errors.nsec ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    placeholder='nsec1...'
                    autoComplete="off"
                  />
                  {errors.nsec && (
                    <p className="text-sm text-red-500">{errors.nsec}</p>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button
                    type="submit"
                    disabled={isLoading || !nsec.trim()}
                    className="flex-1"
                  >
                    {isLoading ? 'Verifying...' : 'Log in'}
                  </Button>

                  <input
                    type="file"
                    accept=".txt"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isFileLoading}
                    className="px-3"
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                </div>

                {errors.file && (
                  <p className="text-sm text-red-500 text-center">{errors.file}</p>
                )}
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
